import { useState, useEffect, useRef } from 'react'
import { IOfferSpendFilters, IFilterConfig, IOfferSpendByAccountFilters } from '../types'
import { constructNewFilterFromConfig, constructUserFiltersFromUrl } from '../components/filters'
import * as lib from '../lib'
import { EFilterOperator, EFilterType, EFilterSpecOperator, EFilterFetchConvertType } from '../types/enum'
import { useAtomValue } from 'jotai'
import * as state from '../states'
import { useSearchParams } from 'react-router'
import dt from 'dayjs'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'

type IUseOfferSpendByAccountFiltersPage = {
	filter: [string, any][];
	send: boolean;
	activeFiltersCount: number;
	filterFillFactor: number;
	isFiltersPanelOpen: boolean;
	setIsFiltersPanelOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
	offerSpendByAccountFiltersConfig: IFilterConfig<keyof IOfferSpendByAccountFilters>[];
	defaultFiltersValue: IOfferSpendByAccountFilters;
	initialFiltersValue: IOfferSpendByAccountFilters;
	clearChildFilters: number;
	setUserFilters: (value: (((prevState: (IOfferSpendByAccountFilters | null)) => (IOfferSpendByAccountFilters | null)) | IOfferSpendByAccountFilters | null)) => void;
}

const SPEND_BY_ACCOUNT_FILTERS_DEFAULT: IOfferSpendByAccountFilters = {
	accountId: '',
	'account.auth.login': '',
	'role.inGroup': '',
	'account.extra.keitaroLogin': '',
	at: [dt().subtract(3, 'day').startOf('day').format('YYYY-MM-DD'), dt().startOf('day').format('YYYY-MM-DD')],
}

export const useOfferSpendByAccountFiltersPage = (): IUseOfferSpendByAccountFiltersPage => {
	const { t } = useTranslation()
	const isFirstRender = useRef<boolean>(true)
	const [searchParams, setSearchParams] = useSearchParams()

	const profile = useAtomValue(state.profileAtom)

	const urlFilter = (() => {
		try {
			return lib.filterSearchParams(searchParams.get('f')) ?? []
		} catch (error) {
			console.error(error)
			return []
		}
	})()

	const [filter, setFilter] = useState<[string, any][]>([...urlFilter]) // main filter with mixins
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
	const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)

	const [clearChildFilters, setClearChildFilters] = useState<number>(0)
	const [send, setSend] = useState<boolean>(false)
	const [userFilters, setUserFilters] = useState<IOfferSpendByAccountFilters | null>(null)

	const filterFillFactor = !!userFilters ? (100 * activeFiltersCount / Object.keys(userFilters).length) : 0

	const offerSpendByAccountFiltersConfig: IFilterConfig<keyof IOfferSpendByAccountFilters>[] = [
		{
			name: 'accountId',
			label: t(ALL_LANG_KEY.offer_spend_by_account_list_table.column_account_id),
			type: EFilterType.input,
			attr: { type: 'number' },
			operator: EFilterOperator.isIntLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'account.auth.login',
			label: t(ALL_LANG_KEY.offer_spend_by_account_list_table.column_account),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'role.inGroup',
			label: t(ALL_LANG_KEY.offer_spend_by_account_list_table.column_account_in_group),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'account.extra.keitaroLogin',
			label: t(ALL_LANG_KEY.offer_spend_by_account_list_table.column_kt_group),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'at',
			label: t(ALL_LANG_KEY.offer_spend_by_account_list_table.column_date),
			type: EFilterType.dateRange,
			operator: EFilterOperator.isBetween,
			styles: { width: '280px' },
			isClearable: false,
		},
	]

	const initialUserFilters = constructUserFiltersFromUrl(
		urlFilter,
		offerSpendByAccountFiltersConfig,
		SPEND_BY_ACCOUNT_FILTERS_DEFAULT
	)

	useEffect(() => {
		let _userFilters = userFilters
		//console.log('hook.useOfferSpendByAccountFiltersPage useEffect::START:: ', { userFilters, filter })
		if (!!_userFilters) {
			setSend(true)

			const { newFilter, activeFiltersCnt } = constructNewFilterFromConfig(
				_userFilters,
				offerSpendByAccountFiltersConfig
			)
			const newFilterStr = lib.filterSetup([...newFilter ?? []])

			setSearchParams((prev) => {
				if (newFilterStr) {
					prev.set('f', newFilterStr)
				} else {
					prev.delete('f')
				}

				// after first pageLoad/pageRefresh resetting is not needed
				if (!isFirstRender.current) {
					prev.delete('page')
				} else {
					isFirstRender.current = false
				}

				return prev
			})

			setFilter(newFilter)
			setActiveFiltersCount(activeFiltersCnt)

			// to open FiltersPanel if there were previously active filters on the page [onRefreshPage case]
			if (activeFiltersCnt > 0) {
				setIsFiltersPanelOpen(true)
			}
			//console.log('hook.useOfferSpendByAccountFiltersPage:: useEffect::END:: ', { userFilters, filter, newFilter, newFilterStr })
		}
	}, [userFilters])

	return {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		offerSpendByAccountFiltersConfig,
		defaultFiltersValue: SPEND_BY_ACCOUNT_FILTERS_DEFAULT,
		initialFiltersValue: initialUserFilters,
		clearChildFilters,
		setUserFilters
	}
}